import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { compressImageForUpload } from '../../../../utils/imageUtils';
import { API_URL, MOMENTS } from '../constants';
import { MAX_UPLOAD_MB, MAX_UPLOAD_BYTES } from '../../../../config/uploadLimits';

/**
 * Manages all try-on state: photo selection/upload, try-on API calls,
 * moment generation, history, caching, and the stage display.
 *
 * @param {Object} params
 * @param {Object} params.session - Current session object from useSession
 * @param {Function} params.setSession - Setter to update session (e.g. tryons_used)
 * @param {Array}  params.displayedGarments - Current paginated garment list
 * @param {number} params.selectedGarmentIndex - Active carousel index
 * @param {Function} params.setSoftCart - Adds garment to soft cart on try-on
 */
export function useTryOn({ session, setSession, displayedGarments, selectedGarmentIndex, setSoftCart }) {
  // Stage display state
  const [stageAsset, setStageAsset] = useState(null);
  const [stageMode, setStageMode] = useState('customer_photo');

  // Session history (customer photos + try-ons + moments)
  const [history, setHistory] = useState([]);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(-1);

  // Try-on state
  const [tryOnLoading, setTryOnLoading] = useState(false);
  const [tryOnCache, setTryOnCache] = useState({});
  const [lastTryOnAsset, setLastTryOnAsset] = useState(null);

  // Photo state
  const [humanImage, setHumanImage] = useState(null);
  const [customerPhotoS3Key, setCustomerPhotoS3Key] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // Moments state
  const [generatedMoments, setGeneratedMoments] = useState({});
  const [momentsLoading, setMomentsLoading] = useState({});
  const [momentsPopupOpen, setMomentsPopupOpen] = useState(false);

  // Size recommendation history (keyed by productId)
  const [sizeHistory, setSizeHistory] = useState({});

  // UI modals/overlays
  const [tryonLimitReached, setTryonLimitReached] = useState(false);
  const [productDetailModal, setProductDetailModal] = useState(null);
  const [fullscreenGallery, setFullscreenGallery] = useState(null);

  // Refs
  const prevSelectedGarmentIndexRef = useRef(null);
  const justCompletedTryOnRef = useRef(false);
  const pendingPhotoUploadRef = useRef(null);
  const photoGenerationRef = useRef(0);
  const fileInputRef = useRef(null);

  // ─── Auto-show cached try-on when carousel garment changes ───────────────
  useEffect(() => {
    if (tryOnLoading) return;

    // Skip if a try-on/moment was just completed (prevents overwriting explicit result)
    if (justCompletedTryOnRef.current) {
      justCompletedTryOnRef.current = false;
      prevSelectedGarmentIndexRef.current = selectedGarmentIndex;
      return;
    }

    // Skip if same garment (no change)
    if (prevSelectedGarmentIndexRef.current === selectedGarmentIndex) return;
    prevSelectedGarmentIndexRef.current = selectedGarmentIndex;

    if (!humanImage || displayedGarments.length === 0) return;

    const selectedGarment = displayedGarments[selectedGarmentIndex];
    if (!selectedGarment) return;

    const productId = selectedGarment.product_id || selectedGarment.id;
    const cachedResult = tryOnCache[productId];

    if (cachedResult) {
      setStageAsset(cachedResult.url || cachedResult);
      setStageMode('tryon');
      setLastTryOnAsset(cachedResult);
      if (cachedResult.moments) setGeneratedMoments(cachedResult.moments);
    } else if (humanImage) {
      const preview = URL.createObjectURL(humanImage);
      setStageAsset(preview);
      setStageMode('customer_photo');
      setGeneratedMoments({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGarmentIndex, tryOnLoading]);

  // ─── Background photo upload (S3) ────────────────────────────────────────
  const startBackgroundPhotoUpload = useCallback(
    (file, sessionToken) => {
      const generation = photoGenerationRef.current;
      const contentType = file.type || 'image/jpeg';

      const promise = fetch(
        `${API_URL}/api/public-studio/session/get-upload-url?content_type=${encodeURIComponent(contentType)}`,
        { method: 'POST', headers: { 'X-Session-Token': sessionToken } }
      )
        .then((r) => { if (!r.ok) throw new Error('Upload URL failed'); return r.json(); })
        .then(({ upload_url, s3_key }) =>
          fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file }).then((r) => {
            if (!r.ok) throw new Error('S3 upload failed');
            return s3_key;
          })
        );

      pendingPhotoUploadRef.current = promise;
      setPhotoUploading(true);

      promise
        .then((s3_key) => {
          if (photoGenerationRef.current === generation) {
            setCustomerPhotoS3Key(s3_key);
            pendingPhotoUploadRef.current = null;
          }
        })
        .catch((err) => console.warn('[NanoKart] Background photo upload failed:', err))
        .finally(() => {
          if (photoGenerationRef.current === generation) setPhotoUploading(false);
        });
    },
    []
  );

  // Shared photo state reset (called when a new photo is selected)
  const resetPhotoRelatedState = () => {
    setTryOnCache({});
    setLastTryOnAsset(null);
    setGeneratedMoments({});
    setMomentsPopupOpen(false);
    prevSelectedGarmentIndexRef.current = null;
    setStageMode('customer_photo');
    setSizeHistory({});
    setCustomerPhotoS3Key(null);
    photoGenerationRef.current += 1;
    pendingPhotoUploadRef.current = null;
  };

  // ─── Image select from file input ────────────────────────────────────────
  const handleImageSelect = useCallback(
    async (e) => {
      const rawFile = e.target.files?.[0];
      if (e.target) e.target.value = '';
      if (!rawFile) return;

      if (rawFile.size > MAX_UPLOAD_BYTES) {
        toast.error(`Image must be under ${MAX_UPLOAD_MB}MB`);
        return;
      }

      setFullscreenGallery(null);
      resetPhotoRelatedState();

      const file = await compressImageForUpload(rawFile);
      setHumanImage(file);
      const preview = URL.createObjectURL(file);
      setStageAsset(preview);

      const customerPhotoItem = {
        id: `customer_photo_${Date.now()}`,
        url: preview,
        thumbnail: preview,
        type: 'customer_photo',
        label: 'Your Photo',
        createdAt: new Date().toISOString(),
      };
      setHistory((prev) => [customerPhotoItem, ...prev]);
      setSelectedHistoryIndex(0);

      if (session?.session_token) {
        startBackgroundPhotoUpload(file, session.session_token);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, startBackgroundPhotoUpload]
  );

  // ─── Camera capture ───────────────────────────────────────────────────────
  const handleCameraCapture = useCallback(
    async (file) => {
      resetPhotoRelatedState();

      const compressedFile = await compressImageForUpload(file);
      const compressedPreview = URL.createObjectURL(compressedFile);
      setHumanImage(compressedFile);
      setStageAsset(compressedPreview);

      const customerPhotoItem = {
        id: `customer_photo_${Date.now()}`,
        url: compressedPreview,
        thumbnail: compressedPreview,
        type: 'customer_photo',
        label: 'Your Photo',
        createdAt: new Date().toISOString(),
      };
      setHistory((prev) => [customerPhotoItem, ...prev]);
      setSelectedHistoryIndex(0);

      if (session?.session_token) {
        startBackgroundPhotoUpload(compressedFile, session.session_token);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, startBackgroundPhotoUpload]
  );

  // ─── Reset current photo (keep history) ──────────────────────────────────
  const resetPhoto = useCallback(() => {
    setHumanImage(null);
    setStageAsset(null);
    setTryOnCache({});
    setLastTryOnAsset(null);
    setSelectedHistoryIndex(-1);
    setGeneratedMoments({});
    setMomentsPopupOpen(false);
    prevSelectedGarmentIndexRef.current = null;
    setStageMode('customer_photo');
  }, []);

  // ─── Virtual Try-On ───────────────────────────────────────────────────────
  const handleTryOn = useCallback(
    async () => {
      if (!humanImage || !session || displayedGarments.length === 0) return;

      const selectedGarment = displayedGarments[selectedGarmentIndex];
      if (!selectedGarment) return;

      const productId = selectedGarment.product_id || selectedGarment.id;
      const cachedResult = tryOnCache[productId];

      // Use cached result if available
      if (cachedResult) {
        justCompletedTryOnRef.current = true;
        setStageAsset(cachedResult.url);
        setLastTryOnAsset(cachedResult);
        setSelectedHistoryIndex(history.findIndex((h) => h.id === cachedResult.id));
        setGeneratedMoments(cachedResult.moments || {});
        setProductDetailModal(null);
        toast.success('Showing your saved try-on!');
        return;
      }

      if ((session.tryons_used || 0) >= (session.max_tryons || 5)) {
        setTryonLimitReached(true);
        return;
      }

      setTryOnLoading(true);
      setProductDetailModal(null);

      try {
        // Resolve S3 key — reuse background upload, wait for in-progress, or upload fresh
        let s3_key = customerPhotoS3Key;

        if (!s3_key) {
          if (pendingPhotoUploadRef.current) {
            try {
              s3_key = await pendingPhotoUploadRef.current;
              setCustomerPhotoS3Key(s3_key);
            } catch { /* fall through to inline upload */ }
          }

          if (!s3_key) {
            const contentType = humanImage.type || 'image/jpeg';
            const uploadUrlResponse = await fetch(
              `${API_URL}/api/public-studio/session/get-upload-url?content_type=${encodeURIComponent(contentType)}`,
              { method: 'POST', headers: { 'X-Session-Token': session.session_token } }
            );
            if (!uploadUrlResponse.ok) {
              const errData = await uploadUrlResponse.json();
              throw new Error(errData.detail || 'Failed to get upload URL');
            }
            const { upload_url, s3_key: newKey } = await uploadUrlResponse.json();
            const uploadResponse = await fetch(upload_url, {
              method: 'PUT',
              headers: { 'Content-Type': contentType },
              body: humanImage,
            });
            if (!uploadResponse.ok) throw new Error('Failed to upload image to storage');
            s3_key = newKey;
            setCustomerPhotoS3Key(s3_key);
          }
        }

        // Call try-on endpoint
        const response = await fetch(
          `${API_URL}/api/public-studio/session/tryon-v2?product_id=${encodeURIComponent(productId)}&s3_key=${encodeURIComponent(s3_key)}`,
          { method: 'POST', headers: { 'X-Session-Token': session.session_token } }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Try-on failed');

        const resultUrl = data.tryon_url;
        const newHistoryItem = {
          id: data.cache_key || `tryon-${Date.now()}`,
          url: resultUrl,
          thumbnail: resultUrl,
          type: 'tryon',
          s3_key: data.tryon_key,
          garment: selectedGarment,
          productId,
          moments: {},
          createdAt: new Date().toISOString(),
        };

        justCompletedTryOnRef.current = true;
        setStageAsset(resultUrl);
        setStageMode('tryon');
        setLastTryOnAsset(newHistoryItem);
        setHistory((prev) => [newHistoryItem, ...prev]);
        setSelectedHistoryIndex(0);
        setGeneratedMoments({});
        setTryOnCache((prev) => ({ ...prev, [productId]: newHistoryItem }));

        // Add to soft cart
        setSoftCart((prev) => {
          if (!prev.find((p) => (p.product_id || p.id) === productId)) {
            return [...prev, selectedGarment];
          }
          return prev;
        });

        // Update session tryons_used count if this wasn't a cache hit
        if (!data.is_cache_hit) {
          const newUsed = (session.tryons_used || 0) + 1;
          const maxTryons = session.max_tryons || 5;
          const newSession = { ...session, tryons_used: newUsed };
          setSession(newSession);
          sessionStorage.setItem('instore_session', JSON.stringify(newSession));

          if (newUsed >= maxTryons) {
            toast.success('Try-on created!', { duration: 2000 });
            setTimeout(() => setTryonLimitReached(true), 1500);
            return;
          }
        }

        // Sync soft cart with backend
        try {
          await fetch(
            `${API_URL}/api/public-studio/session/soft-cart/add?session_token=${session.session_token}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ product_id: productId }),
            }
          );
        } catch (e) {
          console.error('Failed to sync soft cart:', e);
        }

        toast.success(data.is_cache_hit ? 'Loaded from cache!' : 'Try-on created!');
      } catch (err) {
        toast.error(err.message);
      } finally {
        setTryOnLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [humanImage, session, displayedGarments, selectedGarmentIndex, tryOnCache, customerPhotoS3Key, history, setSession, setSoftCart]
  );

  // ─── Moment generation ────────────────────────────────────────────────────
  const handleGenerateMoment = useCallback(
    async (momentId) => {
      if (!lastTryOnAsset?.s3_key) {
        toast.error('Generate a try-on first');
        return;
      }

      const cachedMomentUrl = generatedMoments[momentId];
      if (cachedMomentUrl) {
        justCompletedTryOnRef.current = true;
        setStageAsset(cachedMomentUrl);
        setStageMode('moment');
        const historyIndex = history.findIndex(
          (h) => h.type === 'moment' && h.momentType === momentId && h.url === cachedMomentUrl
        );
        if (historyIndex >= 0) setSelectedHistoryIndex(historyIndex);
        setMomentsPopupOpen(false);
        toast.success('Showing your saved moment!');
        return;
      }

      setMomentsLoading((prev) => ({ ...prev, [momentId]: true }));
      try {
        const response = await fetch(
          `${API_URL}/api/public-studio/session/moments?s3_key=${encodeURIComponent(lastTryOnAsset.s3_key)}&moment_type=${momentId}`,
          { method: 'POST', headers: { 'X-Session-Token': session.session_token } }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Moment generation failed');

        const momentUrl = data.image_url;
        const newHistoryItem = {
          id: `moment_${momentId}_${Date.now()}`,
          url: momentUrl,
          thumbnail: momentUrl,
          type: 'moment',
          momentType: momentId,
          parentTryOnId: lastTryOnAsset.id,
          createdAt: new Date().toISOString(),
        };

        justCompletedTryOnRef.current = true;
        setStageAsset(momentUrl);
        setStageMode('moment');
        setHistory((prev) => [newHistoryItem, ...prev]);
        setSelectedHistoryIndex(0);
        setGeneratedMoments((prev) => ({ ...prev, [momentId]: momentUrl }));
        setMomentsPopupOpen(false);

        // Persist moment in try-on cache
        if (lastTryOnAsset.productId) {
          setTryOnCache((prev) => ({
            ...prev,
            [lastTryOnAsset.productId]: {
              ...prev[lastTryOnAsset.productId],
              moments: { ...(prev[lastTryOnAsset.productId]?.moments || {}), [momentId]: momentUrl },
            },
          }));
        }

        const momentInfo = MOMENTS.find((m) => m.id === momentId);
        toast.success(`${momentInfo?.label || 'Moment'} created!`);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setMomentsLoading((prev) => ({ ...prev, [momentId]: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastTryOnAsset, generatedMoments, history, session]
  );

  // ─── History navigation ───────────────────────────────────────────────────
  const handleHistorySelect = useCallback(
    (index) => {
      justCompletedTryOnRef.current = true;
      setSelectedHistoryIndex(index);
      const item = history[index];
      if (item) {
        setStageAsset(item.url);
        if (item.type === 'tryon') {
          setStageMode('tryon');
          setLastTryOnAsset(item);
        } else if (item.type === 'moment') {
          setStageMode('moment');
        } else {
          setStageMode('customer_photo');
        }
      }
    },
    [history]
  );

  // ─── Fullscreen gallery ───────────────────────────────────────────────────
  const openGallery = useCallback(() => {
    if (history.length > 0) {
      setFullscreenGallery({
        images: history,
        initialIndex: selectedHistoryIndex >= 0 ? selectedHistoryIndex : 0,
      });
    }
  }, [history, selectedHistoryIndex]);

  return {
    // Stage
    stageAsset,
    stageMode,
    // History
    history,
    selectedHistoryIndex,
    // Try-on
    tryOnLoading,
    tryOnCache,
    lastTryOnAsset,
    // Photo
    humanImage,
    setHumanImage,
    customerPhotoS3Key,
    photoUploading,
    // Moments
    generatedMoments,
    momentsLoading,
    momentsPopupOpen,
    setMomentsPopupOpen,
    // Size guide
    sizeHistory,
    setSizeHistory,
    // UI modals
    tryonLimitReached,
    setTryonLimitReached,
    productDetailModal,
    setProductDetailModal,
    fullscreenGallery,
    setFullscreenGallery,
    // Refs
    fileInputRef,
    // Handlers
    handleImageSelect,
    handleCameraCapture,
    handleTryOn,
    handleGenerateMoment,
    handleHistorySelect,
    openGallery,
    resetPhoto,
  };
}
